import {
  XmlOfferEncrypterService,
  EncryptedXmlOffer,
  ShamirHash,
  PublicKeyRepository,
  XmlOfferEncrypterServiceError,
  PublicKey
} from '@/Domain/XmlOffer/Encrypter'
import { XmlOffer } from '@/Domain/XmlOffer'
import { split } from 'shamirs-secret-sharing-ts'
import * as crypto from 'crypto-ts'
import { Md5 } from 'ts-md5/dist/md5'
import { Province } from '@/Domain/Procedures'
import NodeRSA from 'node-rsa'

const QUORUM = 2

export class StandardXmlOfferEncrypterService implements XmlOfferEncrypterService {
  private _publicKeyRepository: PublicKeyRepository

  constructor (publicKeyRepository: PublicKeyRepository) {
    this._publicKeyRepository = publicKeyRepository
  }

  async encrypt (xmlOffer: XmlOffer, province: Province): Promise<EncryptedXmlOffer> {
    try {
      const publicKeys = await this._publicKeyRepository.byProvince(province)
      const keysLength = publicKeys.length
      if (keysLength < 2) {
        throw new TypeError('Public keys length not greater than minimun required')
      }
      const encryptedParts = this.divideShamirParts(keysLength, xmlOffer)
      if (encryptedParts.length === 0) {
        throw new TypeError('Error during process')
      }
      const shamirHashes = this.encryptShamirParts(encryptedParts, publicKeys)
      const md5Key = Md5.hashStr(xmlOffer.xmlString()).toString()

      return new EncryptedXmlOffer(
        xmlOffer.batchId(),
        xmlOffer.recordId(),
        md5Key,
        shamirHashes
      )
    } catch (error) {
      return Promise.reject(new XmlOfferEncrypterServiceError(error.message))
    }
  }

  private divideShamirParts (keysLength: number, xmlOffer: XmlOffer): string[] {
    const secret = Buffer.from(xmlOffer.xmlString())
    const parts = split(secret, { shares: keysLength, threshold: QUORUM })
    return parts.map(current => current.toString('base64'))
  }

  private encryptShamirParts (parts: string[], keys: PublicKey[]): ShamirHash[] {
    const shamirHashes: ShamirHash[] = []
    for (let index = 0; index < parts.length; index++) {
      const currentPart = parts[index]
      const currentKey = keys[index]
      shamirHashes.push(this.encryptSingleShamirPart(currentPart, currentKey))
    }
    return shamirHashes
  }

  private encryptSingleShamirPart (shamirPart: string, publicKey: PublicKey): ShamirHash {
    const randomKey = this.makeid(10)
    const cipherText = crypto.AES.encrypt(shamirPart, randomKey).toString()
    const key = new NodeRSA(publicKey.keyValue(), 'pkcs8-public', { encryptionScheme: 'pkcs1' })
    const encryptedKey = key.encrypt(randomKey, 'base64')
    return new ShamirHash(publicKey.employeeId(), encryptedKey, cipherText)
  }

  private makeid (length: number) {
    let result = ''
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    const charactersLength = characters.length
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength))
    }
    return result
  }
}
